/**
 * Prefetch opted-in multirow images after the page has finished loading.
 * Native image URLs, srcsets, sizing, and layout markup remain authoritative.
 */
(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-multirow-prefetch]';
  const MANAGER_NAME = 'themeMultirowMediaPrefetch';
  const controllers = new Map();

  const shouldSkipConnection = () => {
    const connection = window.navigator && window.navigator.connection;
    return Boolean(
      connection &&
        (connection.saveData === true || /^(?:slow-)?2g$/i.test(connection.effectiveType || ''))
    );
  };

  const isMobileViewport = () => window.innerWidth < 750;

  const snapshot = (image, name) => ({
    present: image.hasAttribute(name),
    value: image.getAttribute(name),
  });

  const findRoots = (scope) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...(scope.querySelectorAll?.(ROOT_SELECTOR) || []));
    return [...new Set(roots)];
  };

  const createController = (root) => {
    const images = [...root.querySelectorAll('img[loading="lazy"]')];
    const original = new Map(
      images.map((image) => [
        image,
        {
          loading: snapshot(image, 'loading'),
          fetchpriority: snapshot(image, 'fetchpriority'),
        },
      ])
    );
    const eligible = new Set();
    const urgent = new Set();
    const attempted = new Set();
    const touched = new Map();
    const listeners = [];
    const rafIds = new Set();
    let nearbyObserver;
    let immediateObserver;
    let active;
    let ready = false;
    let destroyed = false;
    let loadListenerAttached = false;

    const markTouched = (image, name) => {
      const names = touched.get(image) || new Set();
      names.add(name);
      touched.set(image, names);
    };

    const setManaged = (image, name, value) => {
      if (image.getAttribute(name) === value) return;
      image.setAttribute(name, value);
      markTouched(image, name);
    };

    const restoreManaged = (image, name, clear = false) => {
      const state = original.get(image)?.[name];
      if (!state) return;
      if (state.present) image.setAttribute(name, state.value);
      else image.removeAttribute(name);
      if (clear) {
        const names = touched.get(image);
        names?.delete(name);
        if (names && !names.size) touched.delete(image);
      }
    };

    const restoreTouched = (image) => {
      const names = touched.get(image);
      if (!names) return;
      for (const name of names) restoreManaged(image, name, true);
    };

    const restorePriority = (image) => {
      if (touched.get(image)?.has('fetchpriority')) restoreManaged(image, 'fetchpriority');
    };

    const nativeRequestInFlight = (image) => Boolean(image.currentSrc && !image.complete);

    const requestFrame = (callback) => {
      let id;
      id = window.requestAnimationFrame(() => {
        rafIds.delete(id);
        if (!destroyed) callback();
      });
      rafIds.add(id);
    };

    const findNextImage = () => {
      for (const image of images) {
        if (!eligible.has(image) || attempted.has(image) || nativeRequestInFlight(image) || image.complete) continue;
        if (image.getBoundingClientRect().bottom <= 0) continue;

        const index = images.indexOf(image);
        if (images.slice(0, index).some(nativeRequestInFlight)) return null;
        return image;
      }
      return null;
    };

    const pump = () => {
      if (destroyed || !ready || active || document.hidden) return;

      const image = findNextImage();
      if (!image) return;
      attempted.add(image);
      active = image;
      nearbyObserver.unobserve(image);
      eligible.delete(image);

      const priority = original.get(image).fetchpriority;
      if (!urgent.has(image) && !(priority.present && priority.value === 'high')) {
        setManaged(image, 'fetchpriority', 'low');
      }
      setManaged(image, 'loading', 'eager');
    };

    const settle = (image) => {
      restoreTouched(image);
      if (active === image) active = null;
      pump();
    };

    const bindImage = (image) => {
      const onLoad = () => settle(image);
      const onError = () => settle(image);
      image.addEventListener('load', onLoad);
      image.addEventListener('error', onError);
      listeners.push([image, 'load', onLoad], [image, 'error', onError]);
    };

    const onNearbyEntries = (entries) => {
      if (destroyed) return;
      for (const entry of entries) {
        if (entry.isIntersecting) eligible.add(entry.target);
        else eligible.delete(entry.target);
      }
      pump();
    };

    const onImmediateEntries = (entries) => {
      if (destroyed) return;
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        urgent.add(entry.target);
        restorePriority(entry.target);
        if (ready) setManaged(entry.target, 'loading', 'eager');
      }
      pump();
    };

    const onPageLoad = () => {
      requestFrame(() =>
        requestFrame(() => {
          ready = true;
          pump();
        })
      );
    };

    const start = () => {
      if (
        destroyed ||
        !images.length ||
        typeof window.IntersectionObserver !== 'function' ||
        !isMobileViewport() ||
        shouldSkipConnection()
      ) return;

      images.forEach(bindImage);
      nearbyObserver = new window.IntersectionObserver(onNearbyEntries, { rootMargin: '3000px 0px' });
      immediateObserver = new window.IntersectionObserver(onImmediateEntries, { rootMargin: '400px 0px' });
      images.forEach((image) => {
        nearbyObserver.observe(image);
        immediateObserver.observe(image);
      });

      if (document.readyState === 'complete') onPageLoad();
      else {
        window.addEventListener('load', onPageLoad, { once: true });
        loadListenerAttached = true;
      }
    };

    const onVisibilityChange = () => {
      if (!document.hidden) pump();
    };

    const destroy = () => {
      if (destroyed) return;
      destroyed = true;
      nearbyObserver?.disconnect();
      immediateObserver?.disconnect();
      if (loadListenerAttached) {
        window.removeEventListener('load', onPageLoad);
        loadListenerAttached = false;
      }
      for (const [image, eventName, listener] of listeners) image.removeEventListener(eventName, listener);
      for (const id of rafIds) window.cancelAnimationFrame(id);
      for (const image of [...touched.keys()]) restoreTouched(image);
      listeners.length = 0;
      eligible.clear();
      urgent.clear();
      attempted.clear();
      rafIds.clear();
      touched.clear();
      active = null;
    };

    return { start, destroy, onVisibilityChange };
  };

  const initRoot = (root) => {
    if (controllers.has(root)) return;
    const controller = createController(root);
    controllers.set(root, controller);
    controller.start();
  };

  const cleanupWithin = (scope) => {
    for (const [root, controller] of [...controllers]) {
      if (scope === root || scope.contains?.(root) || root.contains?.(scope)) {
        controller.destroy();
        controllers.delete(root);
      }
    }
  };

  const scan = (scope = document) => findRoots(scope).forEach(initRoot);
  const existingManager = window[MANAGER_NAME];
  if (existingManager?.scan) {
    existingManager.scan(document);
    return;
  }

  window[MANAGER_NAME] = { scan, cleanupWithin };
  document.addEventListener('shopify:section:load', (event) => scan(event.target || document));
  document.addEventListener('shopify:section:unload', (event) => cleanupWithin(event.target || document));
  document.addEventListener('visibilitychange', () => {
    for (const controller of controllers.values()) controller.onVisibilityChange();
  });
  scan(document);
})();
